import React, { useEffect, useMemo, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Autocomplete,
  Box,
  CircularProgress,
} from '@mui/material';
import dayjs from 'dayjs';
import api from '../../services/api';
import { useWorkspace } from '../../utils/WorkspaceContext';

/**
 * Lightweight dialog to log time against an action item. Posts to the existing
 * /time-logs/ endpoint (attendee is set server-side from the request user).
 *
 * @param {object} props
 * @param {boolean} props.open
 * @param {function} props.onClose
 * @param {function} props.onLogged - called after a successful create
 * @param {dayjs.Dayjs} [props.defaultDate] - date to attribute the log to
 */
const QuickLogDialog = ({ open, onClose, onLogged, defaultDate }) => {
  const { currentWorkspace } = useWorkspace();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [tasks, setTasks] = useState([]);
  const [actionItems, setActionItems] = useState([]);
  const [selectedTask, setSelectedTask] = useState(null);
  const [selectedItem, setSelectedItem] = useState(null);
  const [duration, setDuration] = useState('1');
  const [notes, setNotes] = useState('');

  useEffect(() => {
    if (!open || !currentWorkspace) return;
    const fetchData = async () => {
      setLoading(true);
      try {
        const [tasksRes, itemsRes] = await Promise.all([
          api.get('tasks/', { params: { workspace_id: currentWorkspace.id } }),
          api.get('action-items/'),
        ]);
        setTasks(tasksRes.data?.payload ?? tasksRes.data ?? []);
        setActionItems(itemsRes.data ?? []);
      } catch (error) {
        console.error('Error loading quick-log data:', error);
      }
      setLoading(false);
    };
    fetchData();
  }, [open, currentWorkspace]);

  // Reset selections whenever the dialog re-opens.
  useEffect(() => {
    if (open) {
      setSelectedTask(null);
      setSelectedItem(null);
      setDuration('1');
      setNotes('');
    }
  }, [open]);

  const itemsForTask = useMemo(
    () => (selectedTask ? actionItems.filter((ai) => ai.task === selectedTask.id) : []),
    [actionItems, selectedTask],
  );

  const handleSubmit = async () => {
    if (!selectedItem) return;
    setSaving(true);
    try {
      const date = defaultDate ? dayjs(defaultDate) : dayjs();
      await api.post('time-logs/', {
        action_item: selectedItem.id,
        duration: parseFloat(duration) || 0,
        start_time: date.hour(dayjs().hour()).minute(dayjs().minute()).format('YYYY-MM-DDTHH:mm:ss'),
        notes,
      });
      onLogged?.();
    } catch (error) {
      console.error('Error logging time:', error);
    }
    setSaving(false);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Quick Log</DialogTitle>
      <DialogContent>
        {loading ? (
          <Box display="flex" justifyContent="center" p={4}>
            <CircularProgress />
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <Autocomplete
              options={tasks}
              getOptionLabel={(t) => t.name || ''}
              value={selectedTask}
              onChange={(_, value) => {
                setSelectedTask(value);
                setSelectedItem(null);
              }}
              renderInput={(params) => <TextField {...params} label="Task" />}
            />
            <Autocomplete
              options={itemsForTask}
              getOptionLabel={(ai) => ai.description || ''}
              value={selectedItem}
              onChange={(_, value) => setSelectedItem(value)}
              disabled={!selectedTask}
              renderInput={(params) => <TextField {...params} label="Action Item" />}
            />
            <TextField
              label="Duration (hours)"
              type="number"
              value={duration}
              onChange={(e) => setDuration(e.target.value)}
              inputProps={{ step: 0.25, min: 0 }}
            />
            <TextField
              label="Notes"
              multiline
              rows={2}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
            />
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={saving || !selectedItem || !duration}
        >
          Log
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default QuickLogDialog;
